/**
 * The chat response's report fields.
 *
 * The HTML rides in `report_html` beside `content_type: "html" | "markdown"`, both server-set on the
 * chat WebSocket response. Decided on WIT-252 (15 Sep): the field is named for its format, so a
 * typed-block renderer can later add `report_json` beside it. No REST spec describes either field,
 * because the chat is a WebSocket.
 *
 * Kept as constants rather than spelled inline in the hook and the renderer, so a rename is one line.
 */
export const REPORT_HTML_FIELD = 'report_html';

/**
 * What a starter chip adds to its chat message to ask for a report by id: `report_id`, beside `message`
 * (app-agent#289, `ReportRequestFields`). The server then renders that report with no model in the loop.
 * A message without it goes to the agent as before, and so does an id the server does not know.
 *
 * The id comes from the chip itself (`QuickAction.report_id`), never from the text, so routing is a
 * lookup rather than an inference.
 */
export const REPORT_ID_FIELD = 'report_id';

/**
 * The report's downloads: `exports`, a list beside the HTML (plan, Decision 17). The server writes
 * each one at render time, persists it as an S3 key and resolves it to a presigned URL on every send,
 * so a report reloaded from history still downloads.
 *
 * The shape sent is `{filename, url, mime_type}`, the same as `AgentAttachment`. The server stores
 * `{s3_key, filename, mime_type}`, and the key never reaches the client (confirmed on lib-docs#28).
 * `getReportExport` is where a change to it would land.
 */
export const REPORT_EXPORTS_FIELD = 'exports';

/** The export formats a report action downloads, by MIME type. */
export const REPORT_EXPORT_TYPE = {
  CSV: 'text/csv',
  XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

/** What `content_type` may say. Anything else is treated as markdown, which is today's behaviour. */
export const CONTENT_TYPE = {
  HTML: 'html',
  MARKDOWN: 'markdown',
};

/**
 * The report actions app-desktop recognises, by `data-action`. The sanitiser keeps no other value and
 * the report wrapper dispatches on these, so the two lists cannot drift apart.
 */
export const REPORT_ACTION = {
  PRINT: 'print',
  CSV: 'csv',
  XLSX: 'xlsx',
  PIVOT: 'pivot',
};

/**
 * A report answer's request context: `report_params`, beside `report_id` — the period and angle the server rendered
 * it for (WIT-246). A pivot asks for the same report again by another angle, and takes everything but the angle from
 * here, never from the markup, which only says which angle a button stands for.
 */
export const REPORT_PARAMS_FIELD = 'report_params';

/** The request context a report message sends back beside `report_id` (`ReportRequestFields`); nothing else is sent. */
export const REPORT_PARAM_KEYS = ['from_date', 'to_date', 'angle'];

/** What an angle may be, in the markup and on the wire: a lowercase identifier. Anything else is dropped. */
export const REPORT_ANGLE_PATTERN = /^[a-z][a-z0-9_]{0,31}$/;

/**
 * The actions that download one of the message's exports, and the MIME type each one opens. A download
 * is matched to its export by type, so the data CSV and the figures XLSX (WIT-247) are separate buttons
 * with separate links, and one link expiring disables only its own button.
 */
export const REPORT_DOWNLOAD_ACTION_TYPES = {
  [REPORT_ACTION.CSV]: REPORT_EXPORT_TYPE.CSV,
  [REPORT_ACTION.XLSX]: REPORT_EXPORT_TYPE.XLSX,
};
